import { CUSTOM_CODE_MESSAGE_MAX_BYTES } from '../types/customCodeChart';
import { parseCustomCodeNativeSpec, } from './customCodeNative';
/**
 * PRpillar fork: sandbox for the "Code" chart type.
 *
 * The author's HTML runs in `<iframe sandbox="allow-scripts" srcdoc>` with no
 * `allow-same-origin`, so the frame has an opaque origin: no cookies, no
 * storage, no access to the parent. The CSP below cuts the network. The only
 * way in or out is the MessagePort the host hands over after load.
 */
export const CUSTOM_CODE_IFRAME_SANDBOX = 'allow-scripts';
export const CUSTOM_CODE_CSP = [
    "default-src 'none'",
    "script-src 'unsafe-inline'",
    "style-src 'unsafe-inline'",
    'img-src data: blob:',
    'font-src data:',
    "connect-src 'none'",
    "frame-src 'none'",
    "worker-src 'none'",
    "form-action 'none'",
    "base-uri 'none'",
].join('; ');
/** Messages the export snapshot may carry: an image is larger than a call. */
export const CUSTOM_CODE_EXPORT_MAX_BYTES = 8 * 1024 * 1024;
/** A native spec references query rows by index, so it stays small. */
export const CUSTOM_CODE_RENDER_MAX_BYTES = 256 * 1024;
/** Calls per second a component may make before the host drops them. */
export const CUSTOM_CODE_MAX_CALLS_PER_SECOND = 20;
export const CUSTOM_CODE_PING_INTERVAL_MS = 5000;
export const CUSTOM_CODE_HANG_TIMEOUT_MS = 10000;
/**
 * The `window.lightdash` shim. Plain ES2017, no template literals with
 * `${}` so it can live in a TS string as is. It must not contain the
 * sequence "</script" — the document builder checks that.
 */
export const CUSTOM_CODE_SHIM = `(function () {
    'use strict';
    var port = null;
    var queue = [];
    var dataCallbacks = [];
    var exportCallback = null;
    var lastPayload = null;
    var rows = [];
    var actions = {};
    var nextAction = 1;
    var HANDLER_KEYS = { onClick: 1, onChange: 1, onRowClick: 1 };
    function encode(value, key, depth) {
        if (depth > 40) throw new Error('lightdash.render: spec nested too deep');
        if (typeof value === 'function') {
            if (!HANDLER_KEYS[key]) return undefined;
            var id = nextAction;
            nextAction += 1;
            actions[id] = value;
            return id;
        }
        if (Array.isArray(value)) {
            return value.map(function (item) { return encode(item, null, depth + 1); });
        }
        if (value && typeof value === 'object') {
            var out = {};
            Object.keys(value).forEach(function (k) {
                var encoded = encode(value[k], k, depth + 1);
                if (encoded !== undefined) out[k] = encoded;
            });
            return out;
        }
        return value;
    }
    function runAction(message) {
        var handler = actions[message.id];
        if (typeof handler === 'function') call(handler, message.value);
    }
    function send(message) {
        if (port) port.postMessage(message);
        else queue.push(message);
    }
    function report(error) {
        var message = error && error.message ? error.message : String(error);
        send({ type: 'error', message: message.slice(0, 2000) });
    }
    function call(callback, payload) {
        try { callback(payload); } catch (e) { report(e); }
    }
    function deliver(payload) {
        lastPayload = payload;
        rows = payload.rows || [];
        api.theme = payload.theme;
        api.options = payload.options || {};
        document.documentElement.setAttribute('data-theme', payload.theme);
        for (var i = 0; i < dataCallbacks.length; i += 1) {
            call(dataCallbacks[i], payload);
        }
    }
    function rowIndex(row) {
        if (typeof row === 'number') return row;
        return rows.indexOf(row);
    }
    function snapshot() {
        if (exportCallback) return exportCallback();
        var canvas = document.querySelector('canvas');
        if (canvas) return canvas.toDataURL('image/png');
        var svg = document.querySelector('svg');
        if (svg) {
            var xml = new XMLSerializer().serializeToString(svg);
            return 'data:image/svg+xml;base64,' +
                btoa(unescape(encodeURIComponent(xml)));
        }
        return null;
    }
    function doExport(id) {
        Promise.resolve()
            .then(snapshot)
            .then(function (dataUrl) {
                send({
                    type: 'export',
                    id: id,
                    dataUrl: typeof dataUrl === 'string' ? dataUrl : null,
                });
            })
            .catch(function (e) {
                report(e);
                send({ type: 'export', id: id, dataUrl: null });
            });
    }
    var api = {
        version: 1,
        theme: 'light',
        options: {},
        onData: function (callback) {
            if (typeof callback !== 'function') return;
            dataCallbacks.push(callback);
            if (lastPayload) call(callback, lastPayload);
        },
        ready: function () { send({ type: 'ready' }); },
        resize: function (height) {
            var value = Number(height);
            if (isFinite(value) && value > 0) {
                send({ type: 'resize', height: Math.ceil(value) });
            }
        },
        drillDown: function (row, fieldId) {
            var index = rowIndex(row);
            if (!(index >= 0 && index < rows.length)) {
                throw new Error('lightdash.drillDown: pass a row from onData');
            }
            send({
                type: 'drillDown',
                rowIndex: index,
                fieldId: fieldId == null ? null : String(fieldId),
            });
        },
        setFilter: function (fieldId, value) {
            send({ type: 'setFilter', fieldId: String(fieldId), value: value });
        },
        onExport: function (callback) {
            if (typeof callback === 'function') exportCallback = callback;
        },
        render: function (root) {
            actions = {};
            var encoded = encode(root, null, 0);
            send({ type: 'render', spec: { version: 1, root: encoded } });
        },
    };
    Object.defineProperty(window, 'lightdash', {
        value: api,
        writable: false,
        configurable: false,
    });
    window.addEventListener('message', function (event) {
        if (port || event.source !== window.parent) return;
        var data = event.data;
        if (!data || data.type !== 'lightdash/init') return;
        if (!event.ports || !event.ports[0]) return;
        port = event.ports[0];
        port.onmessage = function (e) {
            var message = e.data || {};
            if (message.type === 'lightdash/data') deliver(message);
            else if (message.type === 'lightdash/ping') {
                port.postMessage({ type: 'pong', id: message.id });
            } else if (message.type === 'lightdash/export') {
                doExport(message.id);
            } else if (message.type === 'lightdash/action') {
                runAction(message);
            }
        };
        port.postMessage({ type: 'hello' });
        while (queue.length) port.postMessage(queue.shift());
    });
    window.addEventListener('error', function (event) {
        report(event.error || event.message);
    });
    window.addEventListener('unhandledrejection', function (event) {
        report(event.reason);
    });
})();`;
const BASE_STYLE = `html, body { margin: 0; padding: 0; }
body {
    font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 13px;
    color: #1a1b1e;
    background: transparent;
}
html[data-theme='dark'] body { color: #c1c2c5; }`;
/**
 * Builds the iframe document: CSP first (so it governs everything after it),
 * then the shim, then the author's HTML. A `<meta>` CSP the author adds
 * further down can only narrow the policy, never widen it.
 */
export const buildCustomCodeSrcDoc = (source) => {
    if (/<\/script/i.test(CUSTOM_CODE_SHIM)) {
        throw new Error('custom code shim must not close its script tag');
    }
    return [
        '<!DOCTYPE html>',
        '<html><head>',
        `<meta http-equiv="Content-Security-Policy" content="${CUSTOM_CODE_CSP}">`,
        '<meta charset="utf-8">',
        '<meta name="referrer" content="no-referrer">',
        `<style>${BASE_STYLE}</style>`,
        `<script>${CUSTOM_CODE_SHIM}</script>`,
        '</head><body>',
        source,
        '</body></html>',
    ].join('\n');
};
const byteLength = (value) => {
    try {
        return new TextEncoder().encode(JSON.stringify(value) ?? '').length;
    }
    catch {
        return Infinity;
    }
};
const isRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
const isFilterValue = (value) => value === null ||
    typeof value === 'string' ||
    typeof value === 'boolean' ||
    (typeof value === 'number' && Number.isFinite(value));
/**
 * Everything that comes from the component is untrusted: check the size
 * first, then the exact shape. Anything else is dropped.
 */
export const parseCustomCodeMessage = (data) => {
    if (!isRecord(data) || typeof data.type !== 'string')
        return null;
    const limits = {
        export: CUSTOM_CODE_EXPORT_MAX_BYTES,
        render: CUSTOM_CODE_RENDER_MAX_BYTES,
    };
    const limit = limits[data.type] ?? CUSTOM_CODE_MESSAGE_MAX_BYTES;
    if (byteLength(data) > limit)
        return null;
    switch (data.type) {
        case 'hello':
        case 'ready':
            return { type: data.type };
        case 'pong':
            return typeof data.id === 'number'
                ? { type: 'pong', id: data.id }
                : null;
        case 'resize':
            return typeof data.height === 'number' &&
                Number.isFinite(data.height) &&
                data.height > 0
                ? { type: 'resize', height: Math.min(data.height, 10000) }
                : null;
        case 'error':
            return typeof data.message === 'string'
                ? { type: 'error', message: data.message.slice(0, 2000) }
                : null;
        case 'drillDown':
            return Number.isInteger(data.rowIndex) &&
                (data.fieldId === null || typeof data.fieldId === 'string')
                ? {
                    type: 'drillDown',
                    rowIndex: data.rowIndex,
                    fieldId: data.fieldId,
                }
                : null;
        case 'setFilter':
            return typeof data.fieldId === 'string' && isFilterValue(data.value)
                ? {
                    type: 'setFilter',
                    fieldId: data.fieldId,
                    value: data.value,
                }
                : null;
        case 'export':
            return typeof data.id === 'number' &&
                (data.dataUrl === null ||
                    (typeof data.dataUrl === 'string' &&
                        /^data:image\/(png|jpeg|svg\+xml);base64,/.test(data.dataUrl)))
                ? {
                    type: 'export',
                    id: data.id,
                    dataUrl: data.dataUrl,
                }
                : null;
        case 'render': {
            const parsed = parseCustomCodeNativeSpec(data.spec);
            return 'spec' in parsed
                ? { type: 'render', spec: parsed.spec }
                : {
                    type: 'error',
                    message: `lightdash.render: ${parsed.error}`.slice(0, 2000),
                };
        }
        default:
            return null;
    }
};
/** Sliding one-second window: true while the component is within its budget. */
export const createCustomCodeRateLimiter = (maxPerSecond = CUSTOM_CODE_MAX_CALLS_PER_SECOND, now = () => Date.now()) => {
    const stamps = [];
    return () => {
        const t = now();
        while (stamps.length && t - stamps[0] >= 1000)
            stamps.shift();
        if (stamps.length >= maxPerSecond)
            return false;
        stamps.push(t);
        return true;
    };
};
//# sourceMappingURL=customCodeSandbox.js.map